"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "react-redux";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { discardFailedSyncItem } from "../../lib/discardFailedSyncItem.js";
import { retryOutboxItemNow } from "../../lib/syncManager.js";
import { authenticatedSession } from "../../lib/syncPolicy.js";
import { useToast } from "../../hooks/use-toast";
import { siteSyncDetails } from "../../utils/siteSyncDetails.js";
import {
  Cloud,
  RefreshCw,
  CheckCircle2,
  Circle,
  AlertCircle,
  ClipboardCheck,
  FileText,
  Image,
  ChevronUp,
  ChevronDown,
  WifiOff,
  X,
} from "lucide-react";

const EMPTY_ITEMS: any[] = [];
function operationInfo(item: any, context: any) {
  const type = item.type || item.meta?.requestType || "";
  if (type.startsWith("file"))
    return {
      label: "Image",
      detail:
        item.file?.name || item.meta?.description || "Photo and description",
      Icon: Image,
    };
  if (["site", "site-data", "site-overview"].includes(item.meta?.entityType))
    return { ...siteSyncDetails(item, context), Icon: FileText };
  if (item.meta?.entityType === "work")
    return {
      label: "Work item",
      detail: item.meta?.description || "Audit work record",
      Icon: ClipboardCheck,
    };
  if (item.meta?.entityType === "observation")
    return {
      label: "Observation",
      detail: item.meta?.description || "Description updated",
      Icon: FileText,
    };
  return {
    label: "Audit data",
    detail: item.meta?.description || "Report changes",
    Icon: FileText,
  };
}
export function SyncStatusWidget() {
  const dispatch = useAppDispatch();
  const store = useStore();
  const { success, error: showError } = useToast();
  const siteData = useAppSelector((state) => state.siteData);
  const auditReports = useAppSelector((state) => state.auditReports);
  const loggedIn = useAppSelector(authenticatedSession);
  const {
    isOnline,
    isSyncing,
    outboxItems = EMPTY_ITEMS,
    syncRun,
    lastSyncSucceededAt,
  } = useAppSelector((state) => state.offline);
  const [expanded, setExpanded] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState(true);
  const [confirmRemoval, setConfirmRemoval] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const previousOnline = useRef(isOnline);
  const offlineWork = useRef(false);
  const lastNotified = useRef(lastSyncSucceededAt);
  const trigger = useRef<HTMLButtonElement>(null);
  const [filter, setFilter] = useState("All");
  const pending = useMemo(
    () => outboxItems.filter((item: any) => item.status !== "synced"),
    [outboxItems],
  );
  const failed = useMemo(
    () =>
      pending.filter((item: any) =>
        ["failed", "blocked"].includes(item.status),
      ),
    [pending],
  );
  const completed = syncRun?.completed || 0;
  const total = isSyncing
    ? syncRun?.total || pending.length
    : completed + pending.length;
  const progress = total
    ? Math.min(100, Math.round((completed / total) * 100))
    : 100;
  const status = !isOnline
    ? "Offline"
    : isSyncing
      ? "Syncing"
      : failed.length
        ? "Failed"
        : pending.length
          ? "Pending"
          : "Synced";
  const operations = useMemo(() => {
    const items = new Map(
      (syncRun?.items || []).map((item: any) => [item.id, item]),
    );
    for (const item of outboxItems) items.set(item.id, item);
    return [...items.values()] as any[];
  }, [syncRun, outboxItems]);
  const visible = operations.filter(
    (item) =>
      filter === "All" ||
      (filter === "Synced" && item.status === "synced") ||
      (filter === "Failed" && ["failed", "blocked"].includes(item.status)) ||
      (filter === "Syncing" && item.status === "syncing") ||
      (filter === "Offline" &&
        !["synced", "syncing", "failed", "blocked"].includes(item.status)),
  );
  const retry = (itemId?: string) => {
    if (!loggedIn || !isOnline || navigator.onLine === false || isSyncing)
      return;
    retryOutboxItemNow(dispatch, { getState: store.getState, itemId });
  };
  const removeFailedAction = async (itemId: string) => {
    if (removing || isSyncing) return;
    setRemoving(itemId);
    try {
      await discardFailedSyncItem(dispatch, store.getState, itemId);
      setConfirmRemoval(null);
    } catch (error) {
      showError(
        "Could not remove sync action",
        error instanceof Error ? error.message : "Try again.",
      );
    } finally {
      setRemoving(null);
    }
  };
  const closeDetails = useCallback(() => {
    setExpanded(false);
    requestAnimationFrame(() => trigger.current?.focus());
  }, []);
  useEffect(() => {
    if (!loggedIn) {
      setExpanded(false);
      offlineWork.current = false;
    } else if (!isOnline) {
      if (pending.length) offlineWork.current = true;
    } else if (
      !previousOnline.current &&
      (pending.length || isSyncing || offlineWork.current)
    ) {
      // Keep this panel open through completion, including a fast sync that
      // empties the outbox in the same render as the reconnect notification.
      setExpanded(true);
      setDetailsVisible(true);
      setFilter("All");
      offlineWork.current = false;
    }
    previousOnline.current = isOnline;
  }, [loggedIn, isOnline, isSyncing, pending]);
  useEffect(() => {
    const previous = lastNotified.current;
    lastNotified.current = lastSyncSucceededAt;
    if (
      lastSyncSucceededAt &&
      lastSyncSucceededAt !== previous &&
      loggedIn &&
      isOnline &&
      pending.length === 0 &&
      syncRun?.total > 0 &&
      syncRun.completed === syncRun.total
    ) {
      success(
        "All changes synced",
        "Your queued changes were synced successfully.",
        { duration: 4000 },
      );
    }
  }, [
    lastSyncSucceededAt,
    loggedIn,
    isOnline,
    pending.length,
    syncRun?.total,
    syncRun?.completed,
    success,
  ]);
  useEffect(() => {
    if (!expanded) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDetails();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [expanded, closeDetails]);
  if (!loggedIn) return null;
  const countColour = failed.length
    ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"
    : !pending.length
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
      : isSyncing || isOnline
        ? "bg-blue-100 text-blue-700 dark:bg-brand/40 dark:text-link"
        : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300";
  if (!expanded)
    return (
      <section
        role="status"
        aria-label="EncoAudit sync status"
        title={`${status}${failed.length ? ` · ${failed.length} items need attention` : ""}`}
        className="fixed bottom-4 right-4 z-50 flex max-w-[calc(100vw-2rem)] items-center gap-2 rounded-full border border-slate-200 bg-white py-2 pl-3 pr-2 text-sm shadow-md dark:border-border dark:bg-surface"
      >
        {isSyncing ? (
          <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
        ) : failed.length ? (
          <AlertCircle className="h-4 w-4 text-red-600" />
        ) : !isOnline ? (
          <WifiOff className="h-4 w-4 text-slate-500 dark:text-muted-foreground" />
        ) : !pending.length ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        ) : (
          <Cloud className="h-4 w-4 text-blue-600" />
        )}
        <span
          className={`rounded-full px-2.5 py-1 font-semibold tabular-nums transition-colors ${countColour}`}
        >
          {pending.length ? `${pending.length} pending` : status}
        </span>
        <button
          ref={trigger}
          type="button"
          aria-label="View sync details"
          aria-expanded={false}
          aria-controls="sync-details-panel"
          onClick={() => setExpanded(true)}
          className="flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 dark:hover:bg-accent"
        >
          Details
          <ChevronUp className="h-3 w-3" />
        </button>
      </section>
    );
  return (
    <section
      id="sync-details-panel"
      role="status"
      aria-label="EncoAudit sync status"
      className="fixed bottom-4 right-4 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[560px] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 text-sm shadow-lg dark:border-border dark:bg-surface sm:p-5"
    >
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4 dark:border-border">
        <div className="flex items-center gap-3">
          <Cloud className="h-5 w-5" />
          <div>
            <h2 className="font-semibold">EncoAudit</h2>
            <p className="text-xs text-slate-500 dark:text-muted-foreground">
              Audit workspace · Sync status
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-1 text-xs font-medium ${status === "Failed" ? "bg-red-50 text-red-700" : status === "Synced" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-600"}`}
          >
            {status}
          </span>
          <button
            type="button"
            aria-label="Close sync details"
            onClick={closeDetails}
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-accent dark:text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>
      <div className="py-4">
        <div className="flex items-center gap-2">
          {isSyncing ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : !isOnline ? (
            <WifiOff className="h-4 w-4" />
          ) : status === "Synced" ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          ) : (
            <Cloud className="h-4 w-4" />
          )}
          <h3 className="font-semibold">
            {!isOnline
              ? "Saved locally"
              : isSyncing
                ? "Syncing your changes"
                : failed.length
                  ? "Some changes need attention"
                  : pending.length
                    ? "Changes waiting to sync"
                    : "All changes synced"}
          </h3>
        </div>
        <p className="mt-1 pl-6 text-slate-500 dark:text-muted-foreground">
          {isSyncing
            ? `${completed} of ${total} operations completed`
            : !isOnline
              ? "Your changes are saved on this device. Sync resumes online."
              : failed.length
                ? "Review the details below. Other changes can continue syncing."
                : pending.length
                  ? "Sync starts automatically when a connection is available."
                  : "Your audit data is up to date."}
        </p>
        <div
          role="progressbar"
          aria-label="Sync progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
          className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-card"
        >
          <div
            className="h-full rounded-full bg-emerald-600 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-slate-500 dark:text-muted-foreground">
          {pending.length} pending · {pending.length} operation
          {pending.length === 1 ? "" : "s"} remaining
        </p>
        {lastSyncSucceededAt && (
          <p className="mt-1 text-xs text-slate-500 dark:text-muted-foreground">
            Last successful sync:{" "}
            {new Date(lastSyncSucceededAt).toLocaleString()}
          </p>
        )}
      </div>
      <button
        type="button"
        aria-expanded={detailsVisible}
        aria-controls="sync-operation-details"
        onClick={() => setDetailsVisible((visible) => !visible)}
        className="flex w-full items-center justify-between rounded-md px-1 py-2 font-medium"
      >
        {detailsVisible ? "Hide sync details" : "Show sync details"}
        {detailsVisible ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>
      {detailsVisible && (
        <div
          id="sync-operation-details"
          className="max-h-[min(40vh,280px)] overflow-y-auto py-2"
        >
          {!visible.length && (
            <p className="px-1 py-3 text-xs text-slate-500 dark:text-muted-foreground">
              No operations in this view.
            </p>
          )}
          {visible.map((item) => {
            const { label, detail, Icon } = operationInfo(item, {
              siteData,
              auditReports,
            });
            const error = ["failed", "blocked"].includes(item.status);
            return (
              <div key={item.id} className="flex items-start gap-3 px-1 py-2">
                <Icon className="mt-1 h-4 w-4 shrink-0 text-slate-500 dark:text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{label}</p>
                  <p className="break-words text-xs text-slate-500 dark:text-muted-foreground">{detail}</p>
                  {error && (
                    <p className="mt-1 text-xs text-red-600">
                      {item.lastError}
                    </p>
                  )}
                  {error && confirmRemoval === item.id && (
                    <div className="mt-2 rounded-md border border-red-200 p-2">
                      <p className="text-xs text-red-700 dark:text-red-300">
                        Remove this action? This change will no longer sync.
                        This does not undo local edits or delete data already on
                        the server.
                      </p>
                      <div className="mt-2 flex gap-3">
                        <button
                          type="button"
                          disabled={isSyncing || Boolean(removing)}
                          onClick={() => removeFailedAction(item.id)}
                          className="text-xs font-medium text-red-600 disabled:opacity-50"
                        >
                          {removing === item.id
                            ? "Removing..."
                            : "Confirm removal"}
                        </button>
                        <button
                          type="button"
                          disabled={Boolean(removing)}
                          onClick={() => setConfirmRemoval(null)}
                          className="text-xs font-medium text-slate-600 disabled:opacity-50 dark:text-muted-foreground"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <span
                  aria-label={`Operation ${item.status || "pending"}`}
                  className="mt-1"
                >
                  {item.status === "synced" ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : item.status === "syncing" ? (
                    <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
                  ) : error ? (
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  ) : (
                    <Circle className="h-3 w-3 text-slate-500 dark:text-muted-foreground" />
                  )}
                </span>
                {error &&
                  item.retryable !== false &&
                  isOnline &&
                  !isSyncing && (
                    <button
                      type="button"
                      onClick={() => retry(item.id)}
                      className="text-xs font-medium text-blue-600"
                    >
                      Retry
                    </button>
                  )}
                {error &&
                  outboxItems.some((entry: any) => entry.id === item.id) && (
                    <button
                      type="button"
                      disabled={isSyncing || Boolean(removing)}
                      onClick={() => setConfirmRemoval(item.id)}
                      className="text-xs font-medium text-red-600 disabled:opacity-50"
                      title={
                        isSyncing
                          ? "Wait for syncing to finish"
                          : "Remove this action from the sync queue"
                      }
                    >
                      Remove
                    </button>
                  )}
              </div>
            );
          })}
        </div>
      )}
      <footer className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4 dark:border-border">
        {["All", "Offline", "Syncing", "Synced", "Failed"].map((value) => (
          <button
            type="button"
            key={value}
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
            className={`rounded-full border px-3 py-1.5 text-xs ${filter === value ? "border-slate-400 bg-slate-100 dark:bg-card" : "border-slate-200 text-slate-500 dark:border-border"}`}
          >
            {value}
          </button>
        ))}
        {isOnline &&
          pending.some((item: any) => item.retryable !== false) &&
          !isSyncing && (
            <button
              type="button"
              onClick={() => retry()}
              className="ml-auto rounded-full bg-emerald-600 dark:bg-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-primary-border px-3 py-1.5 text-xs font-medium text-white"
            >
              Sync now
            </button>
          )}
      </footer>
    </section>
  );
}
